import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Moon, Sunrise, Plus, X, ArrowRight, Check } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { http, trackEvent } from "@/lib/api";

const SETTLE = ["Fed", "Rocked", "Held", "Pacifier", "Patted", "Settled independently", "Other"];
const HARDEST = ["Frequent wakings", "Hard to settle", "Early waking", "Bedtime", "Naps", "I'm not sure"];

function fmt12(t) {
  if (!t) return "";
  const [h, m] = t.split(":").map(Number);
  const ap = h >= 12 ? "PM" : "AM";
  const hh = ((h + 11) % 12) + 1;
  return `${hh}:${String(m).padStart(2, "0")} ${ap}`;
}

const timeInputCls =
  "bg-[#1C1B1A] border border-[#3a3835] rounded-xl px-3 py-3 text-[#F7F5F0] text-base w-full focus:outline-none focus:border-[#C2836B]/70 [color-scheme:dark]";

// "What Happened Last Night?" — interactive pre-Clarity conversion tool.
// Anonymous data lives only in the browser until the parent chooses to buy Clarity.
export default function SleepTimeline() {
  const nav = useNavigate();
  const { user } = useAuth();
  const startedRef = useRef(false);
  const finishedFiredRef = useRef(false);
  const [bedtime, setBedtime] = useState("19:30");
  const [morning, setMorning] = useState("06:00");
  const [wakings, setWakings] = useState([{ time: "22:00", settle: "" }]);
  const [finished, setFinished] = useState(false);
  const [typical, setTypical] = useState("");
  const [hardest, setHardest] = useState("");

  const started = () => {
    if (!startedRef.current) { startedRef.current = true; trackEvent("night_form_started", {}); }
  };
  const setWake = (i, patch) => { started(); setWakings((w) => w.map((x, j) => (j === i ? { ...x, ...patch } : x))); };
  const addWake = () => { started(); setWakings((w) => [...w, { time: "", settle: "" }]); };
  const removeWake = (i) => setWakings((w) => w.filter((_, j) => j !== i));

  const finish = () => { setFinished(true); if (!finishedFiredRef.current) { finishedFiredRef.current = true; trackEvent("night_form_completed", {}); } };

  const buildTimeline = () => ({
    bedtime: fmt12(bedtime),
    morning_wake: fmt12(morning),
    wakings: wakings.filter((w) => w.time).map((w) => ({ time: fmt12(w.time), settle: w.settle || "Not sure" })),
    typical, hardest, recorded_at: new Date().toISOString(),
  });

  const goClarity = async () => {
    const timeline = buildTimeline();
    try { localStorage.setItem("isc_timeline", JSON.stringify(timeline)); } catch (e) {}
    if (user?.role === "customer") {
      try { await http.put("/portal/intake/timeline", { timeline }); } catch (e) {}
    }
    nav(user?.role === "customer" ? "/portal/progress" : "/register?next=/portal/progress");
  };

  const wakeCount = wakings.filter((w) => w.time).length;

  return (
    <div className="card-night rounded-2xl p-5 sm:p-7" data-testid="sleep-timeline">
      <div className="flex items-center justify-between mb-1">
        <h3 className="font-display text-xl text-[#F7F5F0]">What Happened Last Night?</h3>
        <Moon size={18} className="text-[#C2836B]" />
      </div>
      <p className="text-[#A39E98] text-sm mb-6">Map out the night in less than a minute.</p>

      {!finished ? (
        <div className="space-y-5">
          {/* Bedtime */}
          <div>
            <label className="text-[#738075] text-xs uppercase tracking-wide">Bedtime</label>
            <input data-testid="timeline-bedtime" type="time" value={bedtime}
              onChange={(e) => { started(); setBedtime(e.target.value); }} className={`${timeInputCls} mt-1.5`} />
          </div>

          {/* Wakings */}
          <div>
            <label className="text-[#738075] text-xs uppercase tracking-wide">Night wakings</label>
            <div className="space-y-3 mt-1.5">
              {wakings.map((w, i) => (
                <div key={i} className="rounded-xl border border-[#2E2C2A] p-3" data-testid={`timeline-waking-${i}`}>
                  <div className="flex items-center gap-2">
                    <input type="time" value={w.time} onChange={(e) => setWake(i, { time: e.target.value })}
                      data-testid={`timeline-waking-time-${i}`} className={timeInputCls} />
                    <button onClick={() => removeWake(i)} aria-label="Remove waking" data-testid={`timeline-remove-${i}`}
                      className="shrink-0 w-11 h-11 grid place-items-center rounded-xl border border-[#3a3835] text-[#A39E98] hover:text-[#F7F5F0] hover:border-[#C2836B]/50 transition-colors">
                      <X size={18} />
                    </button>
                  </div>
                  <div className="flex flex-wrap gap-1.5 mt-2.5">
                    {SETTLE.map((s) => (
                      <button key={s} onClick={() => setWake(i, { settle: s })} data-testid={`timeline-settle-${i}-${s}`}
                        className={`px-3 py-1.5 rounded-full text-xs transition-colors ${w.settle === s ? "bg-[#C2836B] text-[#1a120e]" : "bg-[#1C1B1A] border border-[#3a3835] text-[#A39E98] hover:border-[#C2836B]/50"}`}>
                        {s}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
            <button onClick={addWake} data-testid="timeline-add-waking"
              className="mt-3 w-full py-3 rounded-xl border border-dashed border-[#3a3835] text-[#A39E98] hover:text-[#F7F5F0] hover:border-[#C2836B]/50 transition-colors flex items-center justify-center gap-2 text-sm">
              <Plus size={16} /> Add a waking
            </button>
          </div>

          {/* Morning */}
          <div>
            <label className="text-[#738075] text-xs uppercase tracking-wide flex items-center gap-1.5"><Sunrise size={13} className="text-[#C2836B]" /> Up for the day</label>
            <input data-testid="timeline-morning" type="time" value={morning}
              onChange={(e) => { started(); setMorning(e.target.value); }} className={`${timeInputCls} mt-1.5`} />
          </div>

          <button onClick={finish} data-testid="timeline-finish"
            className="btn-clay w-full py-3.5 rounded-full text-[15px] flex items-center justify-center gap-2">
            See what this shows <ArrowRight size={16} />
          </button>
        </div>
      ) : (
        <div className="space-y-5" data-testid="timeline-result">
          <div className="rounded-xl bg-[#1C1B1A] border border-[#2E2C2A] p-4">
            <div className="flex items-baseline gap-2">
              <span className="font-display text-3xl text-[#F7F5F0]">{wakeCount}</span>
              <span className="text-[#A39E98] text-sm">night waking{wakeCount === 1 ? "" : "s"} · bedtime {fmt12(bedtime)} · up {fmt12(morning)}</span>
            </div>
          </div>

          <div>
            <p className="font-display text-lg text-[#F7F5F0] leading-snug">The number of wakings is only part of the picture.</p>
            <p className="text-[#A39E98] text-[15px] mt-2 leading-relaxed">
              Timing, settling, naps, bedtime, routine and recent changes all shape the night. You've recorded one observation; a sleep journal kept over several nights reveals the pattern, and IS-101 teaches how to read it.
            </p>
          </div>

          {/* Optional light follow-ups */}
          <div className="space-y-3 pt-1">
            <div>
              <span className="text-[#738075] text-xs uppercase tracking-wide">Was this a typical night?</span>
              <div className="flex flex-wrap gap-1.5 mt-1.5">
                {["Yes", "No", "Not sure"].map((o) => (
                  <button key={o} onClick={() => setTypical(o)} data-testid={`timeline-typical-${o}`}
                    className={`px-3 py-1.5 rounded-full text-xs transition-colors ${typical === o ? "bg-[#C2836B] text-[#1a120e]" : "bg-[#1C1B1A] border border-[#3a3835] text-[#A39E98] hover:border-[#C2836B]/50"}`}>{o}</button>
                ))}
              </div>
            </div>
            <div>
              <span className="text-[#738075] text-xs uppercase tracking-wide">What felt hardest?</span>
              <div className="flex flex-wrap gap-1.5 mt-1.5">
                {HARDEST.map((o) => (
                  <button key={o} onClick={() => setHardest(o)} data-testid={`timeline-hardest-${o}`}
                    className={`px-3 py-1.5 rounded-full text-xs transition-colors ${hardest === o ? "bg-[#C2836B] text-[#1a120e]" : "bg-[#1C1B1A] border border-[#3a3835] text-[#A39E98] hover:border-[#C2836B]/50"}`}>{o}</button>
                ))}
              </div>
            </div>
          </div>

          <div className="pt-2">
            <button onClick={goClarity} data-testid="timeline-clarity-cta"
              className="btn-clay w-full py-3.5 rounded-full text-[15px] flex items-center justify-center gap-2">
              Save My Timeline — Create Free Student Account <ArrowRight size={16} />
            </button>
            <p className="text-[#738075] text-xs mt-2.5 text-center flex items-center justify-center gap-1.5">
              <Check size={13} className="text-[#8A998C]" /> Your timeline can come with you, so you won't have to enter it again.
            </p>
            <button onClick={() => setFinished(false)} data-testid="timeline-edit"
              className="w-full text-xs text-[#738075] hover:text-[#A39E98] transition-colors mt-3">Edit my timeline</button>
          </div>
        </div>
      )}
    </div>
  );
}
